import React from 'react';
import styled from 'styled-components';
import { 
  FileText, 
  Newspaper, 
  Eye, 
  TrendingUp,
  ChevronRight,
  MapPin,
  Image,
  Loader2
} from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { useStats } from '../../hooks/useStats';

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.5rem;
  margin-bottom: 2rem;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div`
  background: var(--white);
  border-radius: var(--radius-xl);
  padding: 1.5rem;
  box-shadow: var(--shadow-sm);
  border: 1px solid var(--border-light);
  transition: var(--transition);

  &:hover {
    box-shadow: var(--shadow-lg);
    transform: translateY(-4px);
  }
`;

const StatHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;

  .icon {
    width: 48px;
    height: 48px;
    background: ${props => props.$bg};
    border-radius: var(--radius-md);
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--white);
  }

  .trend {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    color: var(--accent-green);
    font-size: 0.85rem;
    font-weight: 600;
  }
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: 800;
  color: var(--text-dark);
  font-family: var(--font-secondary);
`;

const StatLabel = styled.div`
  font-size: 0.9rem;
  color: var(--text-light);
  margin-top: 0.25rem;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 1.5rem;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: var(--white);
  border-radius: var(--radius-xl);
  padding: 1.5rem;
  box-shadow: var(--shadow-sm);
  border: 1px solid var(--border-light);
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid var(--border-light);

  h3 {
    font-family: var(--font-secondary);
    font-size: 1.25rem;
    color: var(--text-dark);
    font-weight: 700;
  }

  a {
    font-size: 0.9rem;
    color: var(--primary-blue);
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 0.25rem;

    &:hover {
      color: var(--secondary-red);
    }
  }
`;

const ActivityItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1rem 0;
  border-bottom: 1px solid var(--border-light);

  &:last-child {
    border-bottom: none;
  }

  .dot {
    width: 10px;
    height: 10px;
    background: var(--accent-gold);
    border-radius: 50%;
    margin-top: 0.4rem;
    flex-shrink: 0;
  }

  .content {
    flex: 1;

    .title {
      font-weight: 600;
      color: var(--text-dark);
      margin-bottom: 0.25rem;
    }

    .time {
      font-size: 0.85rem;
      color: var(--text-light);
    }
  }
`;

const QuickAction = styled.a`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: var(--background-light);
  border-radius: var(--radius-md);
  margin-bottom: 0.75rem;
  transition: var(--transition);
  border: 2px solid transparent;

  &:hover {
    border-color: var(--accent-gold);
    background: var(--white);
  }

  .icon {
    width: 44px;
    height: 44px;
    background: var(--primary-blue);
    border-radius: var(--radius-md);
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--white);
  }

  .content {
    flex: 1;

    .title {
      font-weight: 600;
      color: var(--text-dark);
    }

    .desc {
      font-size: 0.85rem;
      color: var(--text-light);
    }
  }

  svg:last-child {
    color: var(--text-light);
  }
`;

const AdminDashboard = () => {
  const { stats, recentActivities } = useStats();

  const statCards = [
    { 
      label: 'Communiqués', 
      value: stats.loading ? '...' : stats.communiques, 
      trend: '+2', 
      bg: 'linear-gradient(135deg, #dc2626, #ef4444)', 
      icon: FileText 
    },
    { 
      label: 'Articles', 
      value: stats.loading ? '...' : stats.articles, 
      trend: '+5', 
      bg: 'linear-gradient(135deg, #002664, #004d8f)', 
      icon: Newspaper 
    },
    { 
      label: 'Photos Galerie', 
      value: stats.loading ? '...' : stats.galerie, 
      trend: '+3', 
      bg: 'linear-gradient(135deg, #7c3aed, #a78bfa)', 
      icon: Image 
    },
    { 
      label: 'Conflits Suivis', 
      value: stats.loading ? '...' : stats.conflits, 
      trend: '+1', 
      bg: 'linear-gradient(135deg, #059669, #10b981)', 
      icon: MapPin 
    },
  ];

  return (
    <AdminLayout title="Dashboard">
      <StatsGrid>
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <StatCard key={index}>
              <StatHeader $bg={stat.bg}>
                <div className="icon">
                  <Icon size={24} />
                </div>
                <div className="trend">
                  <TrendingUp size={16} />
                  {stat.trend}
                </div>
              </StatHeader>
              <StatValue>{stat.loading ? <Loader2 size={24} className="animate-spin" /> : stat.value}</StatValue>
              <StatLabel>{stat.label}</StatLabel>
            </StatCard>
          );
        })}
      </StatsGrid>

      <ContentGrid>
        <Card>
          <CardHeader>
            <h3>Activités récentes</h3>
            <a href="#">Voir tout <ChevronRight size={16} /></a>
          </CardHeader>
          {stats.loading ? (
            <ActivityItem>
              <div className="content">
                <div className="time">Chargement...</div>
              </div>
            </ActivityItem>
          ) : recentActivities.length > 0 ? (
            recentActivities.map((activity) => (
              <ActivityItem key={activity.id}>
                <div className="dot" />
                <div className="content">
                  <div className="title">{activity.title}</div>
                  <div className="time">{activity.time}</div>
                </div>
              </ActivityItem>
            ))
          ) : (
            <ActivityItem>
              <div className="content">
                <div className="time">Aucune activité récente</div>
              </div>
            </ActivityItem>
          )}
        </Card>

        <Card>
          <CardHeader>
            <h3>Actions rapides</h3>
          </CardHeader>
          <QuickAction href="/admin/communiques/new">
            <div className="icon">
              <FileText size={20} />
            </div>
            <div className="content">
              <div className="title">Nouveau communiqué</div>
              <div className="desc">Publier un communiqué de presse</div>
            </div>
            <ChevronRight size={20} />
          </QuickAction>
          <QuickAction href="/admin/articles/new">
            <div className="icon">
              <Newspaper size={20} />
            </div>
            <div className="content">
              <div className="title">Nouvel article</div>
              <div className="desc">Rédiger un article</div>
            </div>
            <ChevronRight size={20} />
          </QuickAction>
        </Card>
      </ContentGrid>
    </AdminLayout>
  );
};

export default AdminDashboard;
